import { CampaignsFactory, ContentSectionContainer, DataTable, DialogHandler, EmptyContent, ETaskType, FullBar, GenericWrapperContext, Header, ListsFactory, LoadingContainer, SuppressedEmailsFactory, TasksFactory, TDataTableColumnsData, TDataTableRowData, TGenericListReturn, TGenericReturn, TOrder, TTask, TTaskDownload, UserModel, UsersFactory } from "@cakemail-org/ui-components-v2"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import "./styles.scss"
import { TExports } from "./types"
import { getListingColsData, getListingRows } from "./utils"

export function Exports({ }: TExports) {
    const { t } = useTranslation()
    const genericWrapper = useContext(GenericWrapperContext)

    const [loading, setLoading] = useState(false)
    const [tasks, setTasks] = useState<TGenericListReturn<TTask>>()
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<keyof TTask>("created_on") // expires_on, status

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const callTimeoutRef = useRef<NodeJS.Timeout>(undefined)

    useEffect(() => {
        fetchTasks()
    }, [page, perPage, order, orderBy])

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: any = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    function fetchTasks() {
        setLoading(true)
        clearTimeout(callTimeoutRef.current)
        TasksFactory.list({
            withCount: true,
            page: page,
            perPage: perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
        }).then((data) => {
            const pendingTask = data.data.find((task: TTask) => task.status === "pending")
            if (pendingTask) {
                callTimeoutRef.current = setTimeout(() => {
                    fetchTasks()
                }, 5000)
            }

            setTasks(data)
        }).catch(() => {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as keyof TTask)
    }


    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleDownload(task: TTask, btn: TButtonRef) {
        let service: Function = () => { }
        let params = {}

        const exportId = task?.id
        const listId = task?.payload?.listId
        const campaignId = task?.payload?.campaignId

        switch (task.type) {
            case ETaskType.contacts_export:
                service = ListsFactory.downloadContactsExport
                params = { listId, exportId }
                break
            case ETaskType.campaign_logs_export:
                service = CampaignsFactory.downloadCampaignLogExport
                params = { campaignId, exportId }
                break
            case ETaskType.campaigns_reports_export:
                service = CampaignsFactory.downloadReportExport
                params = { exportId }
                break
            case ETaskType.suppressed_emails_export:
                service = SuppressedEmailsFactory.downloadExport
                params = { exportId }
                break
            case ETaskType.list_logs_export:
                service = ListsFactory.downloadListLogExport
                params = { listId, exportId }
                break
            default:
                console.error("Invalid export type", task.type)
                if (btn) setTimeout(btn.reset, 1000)
                return
        }

        service(params).then((data: TGenericReturn<TTaskDownload>) => {
            btn.reset()
            window.open(data.data.url, "_blank")
        }).catch(() => {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        })
    }

    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(), [])

    const rows: any[] = tasks && tasks.data.map((task: TTask) => {
        return getListingRows({
            task,
            loading,
            users,
            handleDownload
        })
    }) || []


    return <ContentSectionContainer className="exportsListingContainer">
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-export_one")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("exports-heading-tooltip")}
                />}
            />

            <LoadingContainer loading={!tasks}>
                {tasks && tasks.data.length === 0 &&
                    <EmptyContent
                        title={t("exports-listing-empty-title")}
                        desc={t("exports-listing-empty-desc")}
                    />
                }

                {tasks && tasks.data.length > 0 &&
                    <DataTable
                        initState={{
                            rowsPerPage: perPage,
                            order,
                            orderBy,
                        }}
                        totalCount={tasks?.pagination?.count}
                        className={`exportsDataTable ${loading ? "loading" : ""}`}
                        onPaginationChange={handlePaginationChange}
                        onSortChange={handleSortChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                    />
                }

            </LoadingContainer>
        </Stack>
    </ContentSectionContainer>
}